import { AfterViewInit, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faPlus, faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, DataMap, FlexGrid } from '@grapecity/wijmo.grid'

import { BaseModal, Bid, CompetitorBid, Grid } from '../../shared'

@Component({
    templateUrl: './competitor-bids.component.html',
    styleUrls: ['./competitor-bids.component.css'],
    standalone: true,
    imports: [FormsModule, WjGridModule, WjInputModule, FaIconComponent],
})
export class CompetitorBidsComponent extends BaseModal implements OnInit, AfterViewInit {
    faPlus = faPlus
    faTrash = faTrash
    faSpinner = faSpinner

    private cdr = inject(ChangeDetectorRef)

    bid!: Bid
    grid: Grid<CompetitorBid>
    competitorBids: CompetitorBid[] = []
    biddersMap!: DataMap

    constructor() {
        super()
        this.grid = new Grid<CompetitorBid>(this.services, 'id', this).isReadOnly(false).doInitialSelection(false)
    }

    ngOnInit() {
        // Initialize lowEstimate and highEstimate to null if undefined to prevent showing 0
        if (!this.bid.lowEstimate) {
            this.bid.lowEstimate = 0
        }
        if (!this.bid.highEstimate) {
            this.bid.highEstimate = 0
        }

        // Initialize competitorBids from the bid, sorted by bid amount
        if (this.bid.competitorBids && this.bid.competitorBids.length > 0) {
            this.competitorBids = [...this.bid.competitorBids]
                .map(bidder => ({
                    ...bidder,
                    won: bidder.won ?? false,
                }))
                .sort((a, b) => {
                    const aAmount = a.bidAmount ?? 0
                    const bAmount = b.bidAmount ?? 0

                    // If both are 0 (or null/undefined), they're equal
                    if (aAmount === 0 && bAmount === 0) {
                        return 0
                    }

                    // If a is 0, it should go to the bottom
                    if (aAmount === 0) {
                        return 1
                    }

                    // If b is 0, it should go to the bottom
                    if (bAmount === 0) {
                        return -1
                    }

                    // Otherwise, sort normally (lowest to highest)
                    return aAmount - bAmount
                })
        } else {
            this.competitorBids = []
        }

        // Set grid items
        this.grid.items = this.competitorBids
    }

    ngAfterViewInit() {
        // Remove focus from any input that might have auto-focused
        setTimeout(() => {
            const activeElement = document.activeElement as HTMLElement
            if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
                activeElement.blur()
            }
        }, 0)
    }

    addBidder() {
        // If this is the first competitor bid being added, default to won: true
        // Otherwise, default to won: false
        const isFirstBidder = this.competitorBids.length === 0

        const newBidder: CompetitorBid = {
            id: undefined,
            bidId: this.bid.id,
            competitorId: 0,
            bidAmount: 0,
            won: isFirstBidder,
        }
        this.competitorBids.push(newBidder)
        this.sortBidders()
        this.grid.items = this.competitorBids
    }

    toggleWon(bidder: CompetitorBid, event: Event) {
        event.stopPropagation()
        const checkbox = event.target as HTMLInputElement

        if (checkbox.checked) {
            // Uncheck all other bidders
            this.competitorBids.forEach(b => {
                if (b !== bidder) {
                    b.won = false
                }
            })
            bidder.won = true
        } else {
            // Allow unchecking
            bidder.won = false
        }

        // Trigger change detection to update checkboxes without full grid refresh
        this.cdr.detectChanges()
    }

    deleteBidder(bidder: CompetitorBid) {
        const index = this.competitorBids.indexOf(bidder)
        if (index > -1) {
            this.competitorBids.splice(index, 1)
            this.grid.items = this.competitorBids
        }
    }

    sortBidders() {
        this.competitorBids.sort((a, b) => {
            const aAmount = a.bidAmount ?? 0
            const bAmount = b.bidAmount ?? 0

            // If both are 0 (or null/undefined), they're equal
            if (aAmount === 0 && bAmount === 0) {
                return 0
            }

            // If a is 0, it should go to the bottom
            if (aAmount === 0) {
                return 1
            }

            // If b is 0, it should go to the bottom
            if (bAmount === 0) {
                return -1
            }

            // Otherwise, sort normally (lowest to highest)
            return aAmount - bAmount
        })
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) return

        // If bid amount was edited, sort the bidders
        const col = flexGrid.columns[args.col]
        if (col.binding === 'bidAmount') {
            this.sortBidders()
            this.grid.items = this.competitorBids
        }
    }

    protected onBeforeSave(): boolean {
        // Validate that all bidders have a competitor and bid amount
        for (const bidder of this.competitorBids) {
            if (!bidder.competitorId || bidder.competitorId === 0) {
                this.services.notification.warning('All bidders must have a competitor selected')
                return false
            }
            if (!bidder.bidAmount || bidder.bidAmount <= 0) {
                this.services.notification.warning('All bidders must have a valid bid amount')
                return false
            }
        }

        // Check for duplicate competitors
        const competitorIds = this.competitorBids.map(b => b.competitorId)
        const uniqueIds = new Set(competitorIds)
        if (competitorIds.length !== uniqueIds.size) {
            this.services.notification.warning('Each competitor can only appear once per bid')
            return false
        }

        return true
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        // Update the bid's competitorBids
        this.bid.competitorBids = this.competitorBids.map(bidder => ({
            ...bidder,
            bidId: this.bid.id,
        }))

        // Convert 0 values to null for lowEstimate and highEstimate (user didn't enter values)
        if (this.bid.lowEstimate === 0) {
            this.bid.lowEstimate = null as any
        }
        if (this.bid.highEstimate === 0) {
            this.bid.highEstimate = null as any
        }

        // Save the bid with updated competitorBids
        this.services.data.put('api/Bid', this.bid).subscribe({
            next: result => {
                resolveSave(result)
            },
            error: errorResult => {
                this.services.notification.error('The bid changes were not successfully saved')
                rejectSave(errorResult)
            },
        })
    }
}
